import { useCallback, useState } from "react";
import Baby from "babyparse";
import FileDownload from "js-file-download";

/**
 * Fetches paginated export data page-by-page, then maps + serializes + downloads
 * exactly once at the end (never on every batch, and never on a partial dataset) —
 * pages are fetched sequentially since each page's `hasMore` is only known once the
 * previous page has resolved. Calling `mapRows` once on the full dataset (rather than
 * once per batch) also keeps any cross-row logic in `mapRows` (e.g. grouping/aggregating
 * rows by a shared key) correct across page boundaries.
 *
 * `totalCount` (when the backend returns it on page 1) drives a real percentage via
 * `progress`; if it's unavailable, `progress` stays 0 until the download completes.
 *
 * @param {Object} options
 * @param {(page: number, filters: Object) => Promise<{ rows: Array, hasMore: boolean, totalCount?: number }>} options.fetchPage
 * @param {(rows: Array) => Array} options.mapRows - returns [header, ...dataRows] for the full dataset
 * @param {(filters: Object) => string} options.getFileName
 * @param {(err: Error) => void} [options.onError]
 */
const useBatchExport = ({ fetchPage, mapRows, getFileName, onError }) => {
  const [state, setState] = useState({
    status: "idle",
    page: 0,
    fetchedCount: 0,
    totalCount: null,
    progress: 0,
    error: null,
  });

  const start = useCallback(
    async (filters) => {
      setState({ status: "loading", page: 0, fetchedCount: 0, totalCount: null, progress: 0, error: null });
      let page = 1;
      let hasMore = true;
      let allRows = [];
      let totalCount = null;
      let fetchedCount = 0;
      try {
        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchPage(page, filters);
          if (totalCount === null && typeof result.totalCount === "number") {
            totalCount = result.totalCount;
          }
          const rows = result.rows || [];
          allRows = allRows.concat(rows);
          fetchedCount += rows.length;
          hasMore = !!result.hasMore;

          const fetchedPage = page;
          const currentFetchedCount = fetchedCount;
          const currentTotalCount = totalCount;
          const progress = currentTotalCount
            ? Math.min(100, Math.round((currentFetchedCount / currentTotalCount) * 100))
            : 0;
          setState((prevState) => ({
            ...prevState,
            page: fetchedPage,
            fetchedCount: currentFetchedCount,
            totalCount: currentTotalCount,
            progress,
          }));
          page += 1;
        }
        const csvRows = allRows.length ? mapRows(allRows) || [] : [];
        if (csvRows.length <= 1) {
          setState((prevState) => ({ ...prevState, status: "empty" }));
          return;
        }
        FileDownload(Baby.unparse(csvRows), getFileName(filters));
        setState((prevState) => ({ ...prevState, status: "done", progress: 100 }));
      } catch (err) {
        if (onError) {
          onError(err);
        }
        setState((prevState) => ({ ...prevState, status: "error", error: err }));
      }
    },
    [fetchPage, mapRows, getFileName, onError]
  );

  return { ...state, start };
};

export default useBatchExport;
